import { useState, useEffect } from 'react'
// redux
import { useSelector } from 'react-redux'
// hooks
import useUIActions from 'hooks/useUIActions'
import useRouterControl from 'hooks/useRouterControl'
import useMobile from 'hooks/useMobile'
// services
import QRCareCenter from '../services/qrCareCenter'
// utils
import sendGaPush from 'utils/sendGaPush'
// components
import Modal from 'components/atoms/modal/modal'
import Button from 'components/atoms/button/button'
import PopupTop from 'components/atoms/popup-top/popup-top'
// assets
import alertImg from 'assets/images/q-r/alert.png'
// types
import typesSlideOverlay from 'components/organisms/slide-overlay/types-slide-overlay'
import popupTopTypes from 'components/atoms/popup-top/popup-top-types'
import QRCareCenterTypes from '../components/molecules/q-r-care-center-inside/q-r-care-center-inside-types'

export default function useQRCareCenter({
	type,
	secondTimeInLessThanThreeHours,
	lessonId = null
} = {}) {
	// redux
	const {
		session: { email, userWithoutLogin, userId }
	} = useSelector(state => state)

	// state
	const defaultState = {
		QRData: false,
		QRloading: false,
		error: false,
		QRSuccess: false,
		isFacingMode: 'environment'
	}
	const [qrState, setQrState] = useState(defaultState)

	// hooks
	const { showOverlay, hideOverlay, handleShowModal, handleCloseModal, handleShowPopupTop } =
		useUIActions()
	const { routerControl } = useRouterControl()
	const isMobile = useMobile()

	// data
	const constraints = {
		audio: false,
		video: true,
		facingMode: isMobile ? qrState.isFacingMode : { exact: 'user' },
		advanced: [{ torch: true }]
	}

	// state actions
	const setQRData = data => {
		setQrState({
			...qrState,
			QRData: data,
			QRloading: true
		})
	}

	const setError = boolean => {
		setQrState({
			...qrState,
			error: boolean
		})
	}
	const handledLoading = boolean => {
		setQrState({
			...qrState,
			QRloading: boolean
		})
	}

	const setQRSuccess = data => {
		setQrState({
			...qrState,
			QRSuccess: data,
			QRloading: false
		})
	}

	const resetStatus = () => setQrState(defaultState)

	// metrics
	const handleSendGaPush = ({ action = 'clicQRModales', label }) => {
		sendGaPush({ category: 'SaludContinuarediseno', action, label })
	}

	// actions
	const getQRData = (result, error) => {
		if (result) {
			return setQRData(result.text || false)
		}

		if (!!error) setError(!!error)
	}

	const showQROverlay = ({ typeLesson, element = false }) => {
		return showOverlay({
			open: true,
			template: typesSlideOverlay.QR,
			showIconBack: false,
			id: typeLesson,
			element
		})
	}

	const goActivatePlan = () => {
		routerControl({ link: 'https://gimnasios.comfama.com/webinicio/index.asp' })
		hideOverlay()
	}

	const hideAndClose = () => {
		hideOverlay()
		handleCloseModal()
	}

	const showErrorQr = () =>
		handleShowModal(
			<Modal
				title='Hubo un error de lectura'
				description='¿Qué tal si lo intentas de nuevo?'
				imagen={alertImg}
			>
				<Button
					uppercase
					title='VOLVER A INTENTAR'
					classes='pink mt2'
					onClick={() => {
						handleSendGaPush({ label: 'Hubo un error de lectura - VOLVER A INTENTAR' })
						handleCloseModal()
					}}
				/>
			</Modal>
		)

	const showSecundTimeModal = () =>
		handleShowModal(
			<Modal
				title='Hoy ya fuiste a un Centro de Cuidado'
				description='Recuerda que tu plan es de 12 entradas, si ingresas otra vez hoy consumirás una nueva entrada'
				imagen={alertImg}
			>
				<Button
					uppercase
					title='CONTINUAR'
					classes='mt2'
					onClick={() => {
						handleSendGaPush({ label: 'Hoy ya fuiste a un Centro de Cuidado - CONTINUAR' })
						handleCloseModal()
					}}
				/>

				<Button
					uppercase
					title='VOLVER'
					classes='pink mt2'
					onClick={() => {
						handleSendGaPush({ label: 'Hoy ya fuiste a un Centro de Cuidado - VOLVER' })
						hideAndClose()
					}}
				/>
			</Modal>
		)

	// send data
	const setLessonID = ({ type, lessonId }) => {
		if (type === QRCareCenterTypes.careCenter) return null

		return lessonId || null
	}

	const setAttendanceLesson = ({ qrMessage }) => {
		const payload = {
			email: userWithoutLogin ? email : null,
			qrMessage,
			lessonId: setLessonID({ type, lessonId })
		}

		QRCareCenter.setLesson({ payload, userId })
			.then(res => {
				if (res.status === 200) {
					handleShowPopupTop(
						<PopupTop header={false} type={popupTopTypes.success} label={res.message} />
					)
					return setQRSuccess(res.data)
				}

				handledLoading(false)
				showErrorQr()
			})
			.catch(() => {
				resetStatus()
				showErrorQr()
			})
	}

	useEffect(() => {
		const timeShowErrorModal = setTimeout(() => {
			if (qrState.QRData) return true
			showErrorQr()
		}, 1000 * 10)

		if (!qrState.error) {
			return clearTimeout(timeShowErrorModal)
		}

		return () => clearTimeout(timeShowErrorModal)
	}, [qrState])

	useEffect(() => {
		if (!qrState.QRData) return

		setAttendanceLesson({ qrMessage: qrState.QRData })
	}, [qrState.QRData])

	useEffect(() => {
		if (!secondTimeInLessThanThreeHours) return

		showSecundTimeModal()
	}, [secondTimeInLessThanThreeHours])

	const actions = {
		showQROverlay,
		setQRData,
		showErrorQr,
		setError,
		goActivatePlan,
		showSecundTimeModal,
		getQRData
	}

	return {
		...actions,
		...qrState,
		constraints
	}
}
